/**
 * subscriptionPortalViewModel — `(state, selectedUser)` → les props du portail Abonnement.
 *
 * Port littéral du corps de `ParentSubscriptionPortalScreen.js` : mêmes gardes, mêmes libellés,
 * même ordre. Données (pattern A, redux) : `currentUser.object` + `formData.user` fusionnés comme
 * le `mapStateToProps` legacy (`:1885-1938`), `formData.schoolSubjects`, `formData.pauseBalance`,
 * `formData.billingPortalUrl`, `state.data.family.isParentMode`, `staticData.summer_config`.
 *
 * `selectedUser` est un ARGUMENT et non une lecture du store : l'enfant affiché vit dans
 * `ParentLayoutContext`, côté legacy, et c'est l'enveloppe du pont qui le descend — `ports/**` n'a
 * pas le droit d'importer `src/common/**`.
 *
 * **Toutes les gardes sortent d'ici en booléens.** L'écran les traduit en « rappel absent = ligne
 * absente » ; le design system, lui, ne connaît aucune de ces règles.
 *
 * Non porté, volontairement (cockpit Été hors MVP) : le bandeau `renderSummerSlot`, le récap
 * `renderSummerSection` et le bouton « Faire mes choix pour l'été » de la section Gestion. Les
 * PRÉDICATS d'été restent, eux, en place : ils décident de trois choses qui, elles, sont portées.
 *
 * Mémoïsé (`createSelector`) sur les tranches qu'il lit : l'onglet reste monté derrière les autres,
 * et sans cela chaque action du store rejouerait gardes d'été, `moment` et composition des textes.
 */
import moment from "moment"
import { createSelector } from "reselect"

import {
    ACTIVATION_FLEXIBILITY_POINTS,
    canExtendProduct,
    canonicalSlotsToGridSlots,
    formatActivationDate,
    formatSlotsLabel,
    getActivationHeadline,
    getActivationPriceLine,
    getCoverageGap,
    getDiscountUrgency,
    getProductEndLine,
    gridDisplayOffsetHours,
    isProductCoverageOver,
    mergeUpcomingActivation,
    shouldAnnounceCoverageGap,
} from "../../../adapters/parent/shared/activationDisplay"
import { type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import {
    type ApiPortalUser,
    type ApiPauseBalance,
    holdsSubscription,
    hoursPerWeekLabel,
    isPauseActive,
    isPausePlanned,
    isSubscribed,
    isSubscriptionPaused,
    isSubscriptionRecurring,
    isSubscriptionOver,
    isSubscriptionTrialing,
    offerLabelFor,
    statusLabelFor,
    statusNameFor,
    toPlannedSubjectObjectives,
    toSubscriptionObjectives,
    toSubscriptionStateLines,
} from "../../../adapters/parent/Subscription/subscriptionAdapter"
import {
    isCommitmentInSummerCockpitWindow,
    isConfirmedSummerStatus,
    isSummer2026Visible,
    resolveDisplayResumeDate,
} from "../../../adapters/parent/Subscription/summerGates"
import { isBlockOn, TENANT_BLOCKS } from "../../../../../data/utility/tenantBlocks"
import { elide } from "../../../ui/formatters/childFormatter"
import { type UISubscriptionLine, type UISubscriptionNextStep, type UISubscriptionObjective } from "../../../ui/types"
import { identityLoading } from "../shared/identityLoading"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

/** Seuil de la carte « Renouvellement à venir » (`ParentSubscriptionPortalScreen.js:108`). */
export const RENEWAL_NOTIF_THRESHOLD_DAYS = 60

/** L'enfant affiché, tel que `ParentLayoutContext` le porte (`storedTokens`). */
export type PortalSelectedUser = { userId?: string | number; firstname?: string; email?: string } | null | undefined

export type SubscriptionPortalViewModel = {
    /**
     * Rien à montrer encore pour CET enfant — le squelette de la page. Le portail est celui du compte
     * connecté : il n'attend que l'identité (`identityLoading`).
     */
    loading: boolean
    error: unknown

    // ── Carte d'état
    offerLabel: string | null
    hoursLabel: string | null
    statusLabel: string | null
    endLine: string | null
    objectives: UISubscriptionObjective[]
    stateLines: UISubscriptionLine[]

    // ── « Renouvellement à venir »
    renewalNotice: { title: string; message: string } | null

    // ── « Et ensuite ? »
    nextStep: UISubscriptionNextStep | null
    canActivateNext: boolean
    canEditNext: boolean
    canExtend: boolean
    extendLoading: boolean
    /** État dégradé : l'offre n'est pas calculable, le support prend le relais. */
    canSupportNextStep: boolean

    // ── « Gestion »
    /** « Modifier mes créneaux » — sous la garde des autres lignes de la section. */
    canChangeSlots: boolean
    canPause: boolean
    canCancelPause: boolean
    cancelPauseLoading: boolean
    canExtraLesson: boolean
    canRequestChange: boolean

    // ── « Facturation »
    canCollectionDay: boolean
    /** Un jour convenu avec l'équipe (la fin de mois) : montré, jamais proposé à la modification. */
    collectionDayAgreed: boolean
    /** Ce que la ligne dit d'un jour convenu — rien pour un jour que le parent règle lui-même. */
    collectionDayNote: string | null
    paymentCollectionDay: number | null
    canPaymentMethod: boolean
    canInvoices: boolean
    canCancel: boolean

    // ── Ce dont les gestes ont besoin (rien de tout ça ne se rend)
    studentFirstName: string
    studentId: string | number | null
    /** Adresse de l'élève — le corps de `studentChangePaymentCollectionDay` la porte. */
    studentEmail: string | null
    activationType: string
    /** `isParentMode && hasPlannedActivation` — la balise « la famille a ouvert sa reprise ». */
    shouldMarkActivationViewed: boolean
    /**
     * Textes de confirmation (le DS ne compose rien). Les deux premiers sont des FONCTIONS : ils
     * ne servent qu'à l'ouverture de leur boîte, et les composer à chaque rendu faisait tourner
     * un `Intl.NumberFormat` et deux `moment` pour un texte que personne ne regardait. Le dernier
     * est deux phrases sur un prénom : il ne coûte rien à composer d'avance.
     */
    cancelPauseConfirm: () => { title: string; message: string }
    /** Absent ⇒ rien à prolonger : c'est la garde que lit le geste. */
    extendConfirm: (() => { title: string; message: string }) | null
    cancelSubscriptionConfirm: { title: string; message: string }
    /** Accusé de l'annulation de pause. */
    pauseCancelledMessage: string
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

/**
 * Fusion des deux charges utilisateur, à l'identique du `mapStateToProps` legacy : `formData.user`
 * (la plus fraîche) s'étale sur `currentUser.object`, puis on relève une à une les clés `summer_*`
 * que seule la seconde porte, et `upcoming_activation` est fusionné clé à clé (un spread écrase
 * un objet imbriqué, et emportait les clés admin avec lui).
 */
export const mergePortalUser = createSelector(
    [(state: ReduxState) => selectDisplayedStudent(state), (state: ReduxState) => state?.data?.formData?.user],
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    (fromCurrentOrNil: any, fromFormOrNil: any): ApiPortalUser => {
        const fromCurrent = fromCurrentOrNil ?? {}
        const fromForm = fromFormOrNil ?? {}

        return {
            ...fromCurrent,
            ...fromForm,
            summer_summer_status: fromCurrent?.summer_summer_status ?? fromForm?.summer_summer_status,
            summer_june_transition: fromCurrent?.summer_june_transition ?? fromForm?.summer_june_transition,
            summer_june_pause_start_date:
                fromCurrent?.summer_june_pause_start_date ?? fromForm?.summer_june_pause_start_date,
            summer_resume_preference: fromCurrent?.summer_resume_preference ?? fromForm?.summer_resume_preference,
            summer_choice_status: fromCurrent?.summer_choice_status ?? fromForm?.summer_choice_status,
            upcoming_activation: mergeUpcomingActivation(
                fromCurrent?.upcoming_activation,
                fromForm?.upcoming_activation
            ),
        }
    }
)

export const subscriptionPortalViewModel = createSelector(
    [
        mergePortalUser,
        (state: ReduxState) => state?.api?.getSubscribePricing,
        (state: ReduxState) => state?.data?.formData?.pauseBalance as ApiPauseBalance | undefined,
        (state: ReduxState) => state?.data?.formData?.schoolSubjects,
        (state: ReduxState) => state?.data?.formData?.billingPortalUrl,
        (state: ReduxState) => !!state?.data?.family?.isParentMode,
        (state: ReduxState) => state?.data?.staticData?.summer_config,
        (state: ReduxState) => !!state?.api?.extendSummerSubscription?.request,
        (state: ReduxState) => !!state?.api?.cancelParentPause?.request,
        (_state: ReduxState, selectedUser: PortalSelectedUser) => selectedUser,
        (state: ReduxState) => state?.data?.staticData?.server_timezone as string | undefined,
        (state: ReduxState) => state?.data?.formData?.user?.id as string | number | undefined,
        (state: ReduxState) => selectDisplayedStudent(state)?.id as string | number | undefined,
    ],
    (
        user,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        pricingReq: any,
        pauseBalance,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        schoolSubjectsOrNil: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        billingPortalUrl: any,
        isParentMode,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        summerConfig: any,
        extendLoading,
        cancelPauseLoading,
        selectedUser,
        serverTimezone,
        formUserId,
        currentUserId
    ): SubscriptionPortalViewModel => {
        const schoolSubjects = schoolSubjectsOrNil ?? []

        const firstName = selectedUser?.firstname || user?.firstname || "votre enfant"
        const isManualSub = !!user?.subscription_state?.is_manual_sub
        const hasActiveSubscription = isSubscribed(user)

        // ── Engagement ────────────────────────────────────────────────────────────────────
        const commitmentEndsAt = user?.commitment_ends_at ? moment(user.commitment_ends_at) : null
        const commitmentActive = !!commitmentEndsAt?.isAfter(moment())
        const commitmentEndsAtFormatted = commitmentActive ? commitmentEndsAt!.format("DD MMMM YYYY") : null
        const daysUntilCommitmentEnds = commitmentActive ? commitmentEndsAt!.diff(moment(), "days") : null

        // ── Cohorte été (les prédicats seuls : aucune surface d'été n'est portée) ──────────
        const isOnGenericTrial = user?.subscription_state?.status === "on_generic_trial"
        const isChurnedSubscription = isSubscriptionOver(user)
        const summerEligible =
            !isManualSub && !isOnGenericTrial && !isChurnedSubscription && !user?.has_summer_fast_track
        const summerVisible = isSummer2026Visible(undefined, summerConfig) && summerEligible
        const isSummerConfirmedBackend = isConfirmedSummerStatus(user?.summer_summer_status)
        const hideCommitmentEndLine =
            summerVisible && isCommitmentInSummerCockpitWindow(user?.commitment_ends_at, summerConfig)

        // ── Activation self-service ───────────────────────────────────────────────────────
        const upcomingActivation = user?.upcoming_activation ?? null
        const activationType = upcomingActivation?.type ?? "none"
        const hasDueDate = !!upcomingActivation && activationType !== "none"
        const activationEligible = !!upcomingActivation?.eligible
        const plannedActivation = upcomingActivation?.planned ?? null
        const recommendedOffer = upcomingActivation?.recommended_offer ?? null
        const hasPlannedActivation = !!plannedActivation

        const canActivateSuite =
            isParentMode && hasDueDate && activationEligible && !hasPlannedActivation && !!recommendedOffer
        const activationOfferUncomputable =
            isParentMode && hasDueDate && activationEligible && !hasPlannedActivation && !recommendedOffer

        // ── Fin du produit en cours ───────────────────────────────────────────────────────
        const productEndDate = upcomingActivation?.due_date ?? null
        const productEndLine = getProductEndLine(activationType, productEndDate)
        const productCoverageOver = !!productEndLine && isProductCoverageOver(productEndDate)

        // ── Trou de couverture → reprise ──────────────────────────────────────────────────
        const effectiveResumeDate = resolveDisplayResumeDate({
            plannedResume: plannedActivation?.effective_at ?? null,
            resolved: upcomingActivation?.resume_date ?? null,
            pauseResume: pauseBalance?.current_pause?.resume_date ?? null,
            summerChoice: user?.summer_resume_date ?? null,
            summerConfig,
        })
        const resumeDateRaw = effectiveResumeDate?.format("YYYY-MM-DD") ?? null
        const {
            hasGap,
            startLabel: gapStartLabel,
            endLabel: gapEndLabel,
        } = getCoverageGap(productEndDate, resumeDateRaw)
        const announceCoverageGap = shouldAnnounceCoverageGap(hasGap, productEndDate)
        const extendableTo = upcomingActivation?.extendable_to ?? null
        const extendableToLabel = formatActivationDate(extendableTo)
        const extendProductLabel = activationType === "stage" ? "le stage" : "les cours d'été"
        const showExtendProduct = !!extendableToLabel && canExtendProduct(extendableTo, hasPlannedActivation)

        const coverageGap = announceCoverageGap
            ? {
                  text: `Aucun live ni prélèvement du ${gapStartLabel} au ${gapEndLabel}`,
                  extendLabel: showExtendProduct ? `Prolonger ${extendProductLabel}` : null,
              }
            : null

        // ── Carte « Renouvellement à venir » ──────────────────────────────────────────────
        const renewalCardVisible =
            !summerVisible &&
            commitmentActive &&
            daysUntilCommitmentEnds !== null &&
            daysUntilCommitmentEnds <= RENEWAL_NOTIF_THRESHOLD_DAYS &&
            !activationEligible &&
            !hasPlannedActivation

        // ── « Et ensuite ? » ──────────────────────────────────────────────────────────────
        // Vue élève, abonnement manuel, ou fenêtre d'été confirmée → aucune carte.
        const nextStepSectionVisible = isParentMode && !isManualSub && !(summerVisible && isSummerConfirmedBackend)
        const nextStep = nextStepSectionVisible
            ? buildNextStep({
                  plannedActivation,
                  activationOfferUncomputable,
                  canActivateSuite,
                  activationType,
                  heldInYear: !!upcomingActivation?.held_in_year,
                  firstName,
                  recommendedOffer,
                  resumeDateRaw,
                  discountDeadlineLabel: getDiscountUrgency(upcomingActivation?.discount_expires_at ?? null)
                      .deadlineLabel,
                  coverageGap,
                  schoolSubjects,
                  // La carte « Reprise planifiée » dit ses créneaux à l'heure de la famille, comme la
                  // grille du composeur qu'ouvre « Modifier » ; mesuré à la DATE DE LA REPRISE.
                  resumeSlotsDisplayOffsetHours: gridDisplayOffsetHours(
                      user?.timezone,
                      serverTimezone,
                      plannedActivation?.effective_at
                  ),
              })
            : null

        // ── Section « Gestion » ───────────────────────────────────────────────────────────
        const isPlannedSubscriptionUpdate = user?.subscription_state?.status === "planned_subscription_update"
        const manageSectionVisible =
            !isManualSub &&
            (hasActiveSubscription ||
                isSubscriptionTrialing(user) ||
                isSubscriptionPaused(user) ||
                isPlannedSubscriptionUpdate)

        const currentPause = pauseBalance?.current_pause ?? null
        const isSummerAutoPause = currentPause?.reason === "summer_auto_pause"
        const manualPausePlanned = isPausePlanned(user) && !isSummerAutoPause && currentPause?.is_cancellable !== false
        const anyPauseInFlight = isPausePlanned(user) || isPauseActive(user)
        // Blocs éteints dans l'espace de l'élève : leurs lignes disparaissent.
        const extraHoursOn = isBlockOn(user, TENANT_BLOCKS.EXTRA_HOURS)
        const subscriptionOn = isBlockOn(user, TENANT_BLOCKS.SUBSCRIPTION)
        const pauseSummerOn = isBlockOn(user, TENANT_BLOCKS.PAUSE_SUMMER)
        const canPause =
            pauseSummerOn &&
            manageSectionVisible &&
            (isSubscriptionRecurring(user) || isSubscriptionTrialing(user)) &&
            !anyPauseInFlight

        // ── Section « Facturation » ───────────────────────────────────────────────────────
        // Une famille en pause garde ses droits : sa reprise reprendra les prélèvements, et elle
        // peut résilier. `paused` n'est pas dans `subscribedStatuses`, d'où ce complément.
        const pausedWithRights = isSubscriptionPaused(user)
        const showDateButton = hasActiveSubscription || pausedWithRights
        const showPortalButtons = !!billingPortalUrl
        const showCancelButton = hasActiveSubscription || pausedWithRights || isSubscriptionTrialing(user)
        const billingSectionVisible = !isManualSub && (showDateButton || showPortalButtons || showCancelButton)
        const showCollectionDay = subscriptionOn && billingSectionVisible && showDateButton
        const collectionDayAgreed = showCollectionDay && user?.payment_collection_day_granted === true

        return {
            loading: identityLoading({ pricing: pricingReq, formUserId, currentUserId }),
            // Le legacy ne montre l'erreur que si aucun abonnement n'a pu être lu (`:1798`).
            error: pricingReq?.error && !user?.subscription_state ? pricingReq.error : null,

            ...buildStateCard({
                user,
                activationType,
                productEndLine,
                productCoverageOver,
                schoolSubjects,
            }),
            stateLines: toSubscriptionStateLines(user, { hideCommitmentEnd: hideCommitmentEndLine }),

            renewalNotice: renewalCardVisible
                ? {
                      title: "Renouvellement à venir",
                      message: `Fin d'engagement le ${commitmentEndsAtFormatted}. Voir les options.`,
                  }
                : null,

            nextStep,
            canActivateNext: nextStep?.variant === "activate",
            canEditNext:
                nextStep?.variant === "activate" || (nextStep?.variant === "planned" && nextStep.editable === true),
            canExtend: nextStep != null && coverageGap?.extendLabel != null,
            extendLoading,
            canSupportNextStep: nextStep?.variant === "uncomputable",

            // « Modifier mes créneaux » : la même garde que la section — le serveur dit ensuite quels
            // cours se changent (aucun cours de la semaine → la feuille le dit).
            canChangeSlots: manageSectionVisible,
            canPause,
            canCancelPause: pauseSummerOn && manageSectionVisible && manualPausePlanned,
            cancelPauseLoading,
            // « Réserver un cours en plus » et « Demander un changement » restent ouverts dès que la
            // section est rendue (le legacy les pose sans autre condition).
            canExtraLesson: extraHoursOn && manageSectionVisible,
            canRequestChange: manageSectionVisible,

            canCollectionDay: showCollectionDay && !collectionDayAgreed,
            collectionDayAgreed,
            collectionDayNote: collectionDayAgreed ? "convenu avec Masteur" : null,
            paymentCollectionDay: user?.payment_collection_day ?? null,
            canPaymentMethod: subscriptionOn && billingSectionVisible && showPortalButtons && !user?.impersonation,
            canInvoices: subscriptionOn && billingSectionVisible && showPortalButtons,
            canCancel: pauseSummerOn && billingSectionVisible && showCancelButton,

            studentFirstName: firstName,
            studentId: selectedUser?.userId ?? user?.id ?? null,
            // Le legacy envoie `data?.email` où `data` est `currentUser.object` : c'est l'élève
            // connecté, pas la chip sélectionnée — en mode parent, c'est le même.
            studentEmail: user?.email ?? selectedUser?.email ?? null,
            activationType,
            shouldMarkActivationViewed: isParentMode && hasPlannedActivation,
            cancelPauseConfirm: () => buildCancelPauseConfirm(selectedUser?.firstname, currentPause),
            extendConfirm: showExtendProduct
                ? () => ({
                      title: `Prolonger ${extendProductLabel} jusqu'au ${extendableToLabel} ?`,
                      message: `${firstName} garde ses lives sans interruption jusqu'au ${extendableToLabel}. La mensualité correspondante sera prélevée comme d'habitude, sur le moyen de paiement déjà enregistré.`,
                  })
                : null,
            cancelSubscriptionConfirm: {
                title: `Résilier l'abonnement ${elide(firstName)}\u00a0?`,
                message:
                    "Vous allez accéder au parcours de résiliation. Aucun changement n'est appliqué tant que vous n'avez pas confirmé toutes les étapes.",
            },
            pauseCancelledMessage: selectedUser?.firstname?.trim()
                ? `La pause ${elide(selectedUser.firstname)} a bien été annulée.`
                : "La pause a bien été annulée.",
        }
    }
)

type StateCardInput = {
    user: ApiPortalUser
    activationType: string
    productEndLine: string | null
    productCoverageOver: boolean
    schoolSubjects: ApiSchoolSubject[]
}

/**
 * La carte d'état ne décrit que le produit qui couvre l'élève aujourd'hui, dans cet ordre : son
 * abonnement s'il court ou est en pause, le stage en cours, son abonnement s'il n'est que planifié
 * (il n'a pas commencé : la reprise a sa carte), sinon rien (« Aucun abonnement actif »).
 *
 * Sans rôle d'abonné, `current_offer` vaut `basic` et `subscription_state` garde le volume de
 * l'abonnement terminé : les lire hors d'un abonnement présentait l'ancien abonnement comme le
 * produit en cours (« Cours partagés · 3h/sem » sur un élève en stage). Et l'échéance d'un abonné
 * peut être son stage : la fin du stage ne s'écrit pas sous un abonnement qui continue.
 */
function buildStateCard(
    input: StateCardInput
): Pick<SubscriptionPortalViewModel, "offerLabel" | "hoursLabel" | "statusLabel" | "endLine" | "objectives"> {
    const { user, activationType, productEndLine, productCoverageOver, schoolSubjects } = input
    const status = user?.subscription_state?.status
    const planned = status === "planned"
    const objectives = toSubscriptionObjectives(user?.pedagogical_objectives, schoolSubjects)

    const subscriptionCard = () => {
        const endLine = activationType === "stage" ? null : productEndLine
        return {
            offerLabel: offerLabelFor(user?.current_offer),
            // Le Mode Autonome n'a pas de volume hebdomadaire à annoncer.
            hoursLabel:
                user?.current_offer === "autonomy"
                    ? null
                    : hoursPerWeekLabel(user?.subscription_state?.hours_per_month),
            // Couverture déjà terminée : la ligne de fin porte l'état à elle seule.
            statusLabel: endLine && productCoverageOver ? null : statusLabelFor(status),
            endLine,
            objectives,
        }
    }

    if (holdsSubscription(user) && !planned) return subscriptionCard()

    if (activationType === "stage" && productEndLine && !productCoverageOver) {
        // Ses heures sont celles de son objectif, en chip ; un stage n'a pas de cadence hebdomadaire.
        return {
            offerLabel: offerLabelFor("stage"),
            hoursLabel: null,
            statusLabel: null,
            endLine: productEndLine,
            objectives,
        }
    }

    if (planned) return subscriptionCard()

    // L'état vide dit déjà « Aucun abonnement actif » : le statut ne s'y ajoute que s'il dit autre
    // chose (« Abonnement expiré »).
    return {
        offerLabel: null,
        hoursLabel: null,
        statusLabel: statusNameFor(status),
        endLine: null,
        objectives: [],
    }
}

type NextStepInput = {
    plannedActivation: NonNullable<ApiPortalUser["upcoming_activation"]>["planned"]
    activationOfferUncomputable: boolean
    canActivateSuite: boolean
    activationType: string
    /** Stage tenu pendant l'année : l'accroche dit « la suite », pas « la rentrée ». */
    heldInYear: boolean
    firstName: string
    recommendedOffer: NonNullable<ApiPortalUser["upcoming_activation"]>["recommended_offer"]
    resumeDateRaw: string | null
    discountDeadlineLabel: string | null
    coverageGap: { text: string; extendLabel: string | null } | null
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    schoolSubjects: any[]
    resumeSlotsDisplayOffsetHours: number
}

/**
 * Les trois états de `renderActivationSuiteCard` (`:1426-1547`), dans le même ordre de décision :
 * jalon posé d'abord, puis offre non calculable, puis suite à activer. Aucun état neutre — un
 * profil non éligible rend `null`, la section n'existe pas.
 */
function buildNextStep(input: NextStepInput): UISubscriptionNextStep | null {
    const {
        plannedActivation,
        activationOfferUncomputable,
        canActivateSuite,
        activationType,
        heldInYear,
        firstName,
        recommendedOffer,
        resumeDateRaw,
        discountDeadlineLabel,
        coverageGap,
        schoolSubjects,
        resumeSlotsDisplayOffsetHours,
    } = input

    if (plannedActivation) {
        const resumeLabel = formatActivationDate(plannedActivation.effective_at)
        const inProgress = !!plannedActivation.is_past_due
        const slotsLine = formatSlotsLabel(
            canonicalSlotsToGridSlots(plannedActivation.resume_slots ?? []),
            resumeSlotsDisplayOffsetHours
        )
        const installments = plannedActivation.loyalty_discount_installments
        const discountTotal = plannedActivation.loyalty_discount_total ?? 0

        return {
            variant: "planned",
            pillLabel: inProgress ? "Reprise en cours d'activation" : "Reprise planifiée",
            pillIcon: inProgress ? "hourglass-half" : "check-circle",
            headline: resumeLabel ? `Reprise le ${resumeLabel}` : "Reprise planifiée",
            offerLine: joinOffer(plannedActivation.offer_label, plannedActivation.hours_per_week),
            priceLine: getActivationPriceLine(plannedActivation.monthly_price, plannedActivation.first_month_price),
            priceDiscounted: isDiscounted(plannedActivation.monthly_price, plannedActivation.first_month_price),
            detailLines: compactLines([
                slotsLine ? `Dispo : ${slotsLine}` : null,
                installments && discountTotal > 0
                    ? `Remise : −50 € sur chacun des ${Math.round(discountTotal / 50)} premiers mois`
                    : null,
            ]),
            objectives: toPlannedSubjectObjectives(
                plannedActivation.subject_ids,
                plannedActivation.hours_allocation,
                schoolSubjects
            ),
            coverageGap,
            // « Modifier » disparaît sur un jalon non modifiable ou déjà en cours d'application.
            editable: plannedActivation.modifiable !== false && !inProgress,
        }
    }

    if (activationOfferUncomputable) {
        return {
            variant: "uncomputable",
            pillLabel: "Et ensuite ?",
            pillIcon: "bolt",
            message: "Nous préparons votre offre de reprise personnalisée. Elle sera disponible ici très bientôt.",
        }
    }

    if (canActivateSuite) {
        const resumeLabel = formatActivationDate(resumeDateRaw)
        const hasLoyalty = !!recommendedOffer?.loyalty_eligible

        return {
            variant: "activate",
            pillLabel: "Et ensuite ?",
            pillIcon: "bolt",
            headline: getActivationHeadline(activationType, firstName, heldInYear),
            offerLine: joinOffer(recommendedOffer?.offer_label, recommendedOffer?.hours_per_week),
            priceLine: getActivationPriceLine(recommendedOffer?.monthly_price, recommendedOffer?.first_month_price),
            priceDiscounted: isDiscounted(recommendedOffer?.monthly_price, recommendedOffer?.first_month_price),
            detailLines: compactLines([
                hasLoyalty && discountDeadlineLabel ? `Tarif valable jusqu'au ${discountDeadlineLabel}` : null,
                resumeLabel ? `Reprise le ${resumeLabel}` : null,
            ]),
            guarantees: ACTIVATION_FLEXIBILITY_POINTS,
            coverageGap,
        }
    }

    return null
}

/** « Cours partagés · 2h/sem ». */
function joinOffer(offerLabel: string | undefined, hoursPerWeek: number | undefined): string | null {
    const line = [offerLabel, hoursPerWeek ? `${hoursPerWeek}h/sem` : null].filter(Boolean).join(" · ")
    return line || null
}

/** La remise ne s'annonce que si elle en est une (1ᵉʳ mois strictement inférieur au mensuel). */
function isDiscounted(monthlyPrice: number | undefined, firstMonthPrice: number | undefined): boolean {
    return firstMonthPrice != null && monthlyPrice != null && firstMonthPrice < monthlyPrice
}

function compactLines(lines: (string | null)[]): string[] {
    return lines.filter((line): line is string => line != null)
}

/**
 * La confirmation d'annulation de pause, phrase par phrase (`confirmCancelManualPause:213-262`) :
 * l'abonnement continue, l'engagement revient à son échéance initiale, et le crédit accordé est
 * retiré. Les deux dernières n'apparaissent que si la balance les porte.
 */
function buildCancelPauseConfirm(
    studentFirstname: string | undefined,
    currentPause: NonNullable<ApiPauseBalance["current_pause"]> | null
): { title: string; message: string } {
    const studentName = studentFirstname?.trim() || ""
    const previousCommitmentLabel = currentPause?.previous_commitment_end_date
        ? moment(currentPause.previous_commitment_end_date).format("DD/MM/YYYY")
        : null
    const appliedCredit = Number(currentPause?.applied_credit ?? 0)
    const creditLabel =
        appliedCredit > 0
            ? new Intl.NumberFormat("fr-FR", {
                  style: "currency",
                  currency: "EUR",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
              }).format(appliedCredit)
            : null

    const lines = [
        studentName
            ? `L'abonnement ${elide(studentName)} continuera normalement.`
            : "Votre abonnement continuera normalement.",
    ]
    if (previousCommitmentLabel) {
        lines.push(
            studentName
                ? `L'engagement ${elide(studentName)} repasse à son échéance initiale du ${previousCommitmentLabel}.`
                : `Votre engagement repasse à son échéance initiale du ${previousCommitmentLabel}.`
        )
    }
    if (creditLabel) {
        lines.push(`Le crédit de ${creditLabel} accordé sera retiré.`)
    }

    return { title: "Annuler la pause planifiée ?", message: lines.join(" ") }
}
